import { createTheme } from '@mui/material'

const typography = {
  fontFamily: 'LINE Seed JP, LINE Seed KR, sans-serif',
}

const shadows = [
  'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
  'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
  'none', 'none', 'none', 'none', 'none'
] as const

// システムバー（ステータスバー / ナビゲーションバー）とブラウザのテーマカラーに使う色。
// status はヘッダー（AppBar color='header'）の背景、navigation はボトムナビゲーションの背景と
// 一致させる。Android 16+ は強制edge-to-edgeでバーが透過し、これらのUIがそのまま枠として
// 見えるため、明示的に色を塗る経路（Android 15以下）と同じ値である必要がある。
// Androidの Color.parseColor は3桁表記を解釈しないので必ず6桁で書くこと。
export const system_bar_colors = {
  light: { status: '#FFFFFF', navigation: '#EBE7F2' },
  dark: { status: '#232223', navigation: '#373637' },
} as const

export type SystemBarColorScheme = keyof typeof system_bar_colors

const light_palette = {
  mode: 'light' as const,
  primary: {
    main: '#8348E8',
    light: '#BA93FF',
    dark: '#6B4C9F',
    contrastText: '#fff'
  },
  success: {
    main: '#83CF00',
    dark: '#83CF00',
    contrastText: '#fff'
  },
  gray_b: {
    main: '#EBE7F2',
    light: '#F4F2F7',
    dark: '#DAD4E4',
    contrastText: '#1C1B1F'
  },
  safe: {
    main: '#83CF00',
    dark: '#83CF00',
    contrastText: '#fff'
  },
  warning: {
    main: '#FF3D00',
    light: '#FFE7E7',
    dark: '#FF3D00',
    contrastText: '#fff',
  },
  text: {
    primary: '#1C1B1F',
    secondary: '#595959',
    disabled: '#878787',
  },
  header: {
    main: system_bar_colors.light.status,
    light: system_bar_colors.light.status,
    contrastText: '#1C1B1F'
  },
}

const dark_palette = {
  mode: 'dark' as const,
  primary: {
    main: '#AD83F5',
    light: '#D4BFFF',
    dark: '#6B4C9F',
    contrastText: '#1C1B1F'
  },
  success: {
    main: '#83CF00',
    dark: '#83CF00',
    contrastText: '#fff'
  },
  gray_b: {
    main: '#373637',
    light: '#595959',
    dark: '#232223',
    contrastText: '#E1E1E1'
  },
  safe: {
    main: '#83CF00',
    dark: '#83CF00',
    contrastText: '#fff'
  },
  warning: {
    main: '#FF3D00',
    light: '#2D1000',
    dark: '#FF3D00',
    contrastText: '#fff',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#E1E1E1',
    disabled: '#878787',
  },
  header: {
    main: system_bar_colors.dark.status,
    light: '#373637',
    contrastText: '#FFFFFF'
  },
  background: {
    default: '#232223',
    paper: '#373637',
  },
}

const shared_component_overrides = {
  MuiSelect: {
    defaultProps: {
      variant: 'filled' as const,
    },
  },
  MuiButton: {
    styleOverrides: {
      sizeSmall: {
        padding: '4px 14px',
      },
      root: {
        fontWeight: 'bold' as const,
        textTransform: 'none' as const,
      }
    }
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        paddingTop: 'env(safe-area-inset-top)',
        height: 'calc(62px + env(safe-area-inset-top))',
        minHeight: 'calc(62px + env(safe-area-inset-top))',
      }
    }
  },
  MuiTab: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          'fontWeight': 'bold',
        },
        textTransform: 'none' as const,
      }
    }
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontSize: '14pt',
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }
    }
  },
  MuiSwitch: {
    styleOverrides: {
      thumb: {
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      }
    },
  },
}

const snackbar_position_overrides = {
  anchorOriginBottomCenter: {
    bottom: 'calc(60px + env(safe-area-inset-bottom))',
  },
  anchorOriginBottomLeft: {
    bottom: 'calc(60px + env(safe-area-inset-bottom))',
    left: 'env(safe-area-inset-left)',
  },
  anchorOriginBottomRight: {
    bottom: 'calc(60px + env(safe-area-inset-bottom))',
    right: 'env(safe-area-inset-right)',
  },
  anchorOriginTopCenter: {
    top: 'calc(62px + env(safe-area-inset-top))',
  },
  anchorOriginTopLeft: {
    top: 'calc(62px + env(safe-area-inset-top))',
    left: 'env(safe-area-inset-left)',
  },
  anchorOriginTopRight: {
    top: 'calc(62px + env(safe-area-inset-top))',
    right: 'env(safe-area-inset-right)',
  },
  // index.cssにanchorOriginTopRightのハックあり
}

const filled_input_base_overrides = {
  borderRadius: '4px',
  paddingLeft: '2px',
  paddingRight: '2px',
  '&.MuiInputBase-sizeSmall > .MuiInputBase-input': {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
} as const

// Light-only theme (used when FEATURE_DARK_MODE is false)
const light_theme = createTheme({
  typography,
  palette: light_palette,
  shadows: [...shadows],
  components: {
    ...shared_component_overrides,
    MuiFilledInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        root: ({ theme }) => ({
          ...filled_input_base_overrides,
          backgroundColor: theme.palette.gray_b.main,
          '&:hover': { backgroundColor: theme.palette.gray_b.dark },
          '&.Mui-focused': { backgroundColor: theme.palette.gray_b.main },
        }),
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 38,
          color: '#1C1B1F',
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#1C1B1F',
        }
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 'calc(60px + env(safe-area-inset-bottom))',
          minHeight: 'calc(60px + env(safe-area-inset-bottom))',
          position: 'fixed',
          paddingBottom: 'env(safe-area-inset-bottom)',
          bottom: 0, left: 0, right: 0,
          backgroundColor: system_bar_colors.light.navigation,
        }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: '#1C1B1F',
        }
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            background: 'white',
            color: 'black',
            boxShadow: '0 4px 4px 0 rgba(0,0,0,0.2)',
            borderRadius: '4px',
          },
          '& .MuiSnackbarContent-message': {
            wordBreak: 'break-word',
          },
        },
        ...snackbar_position_overrides,
      }
    },
  },
})

// Dual color-scheme theme (used when FEATURE_DARK_MODE is true)
const dark_mode_theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data' },
  colorSchemes: {
    light: { palette: light_palette },
    dark: { palette: dark_palette },
  },
  typography,
  shadows: [...shadows],
  components: {
    ...shared_component_overrides,
    MuiFilledInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        // applyStyles('light', ...)であるべき理由はなく、'*:where([data-light]) &'は
        // ルート（html）のdata-lightにもマッチしてしまう。data-darkで強制ダーク化した
        // ダイアログ配下でもこれが勝ってしまい背景だけライトのまま取り残されるため、
        // 他の項目と同じく「素のライト値 + applyStyles('dark', ...)」の形にする。
        root: ({ theme }) => ({
          ...filled_input_base_overrides,
          backgroundColor: light_palette.gray_b.main,
          '&:hover': { backgroundColor: light_palette.gray_b.dark },
          '&.Mui-focused': { backgroundColor: light_palette.gray_b.main },
          ...theme.applyStyles('dark', {
            backgroundColor: dark_palette.gray_b.main,
            '&:hover': { backgroundColor: dark_palette.gray_b.dark },
            '&.Mui-focused': { backgroundColor: dark_palette.gray_b.main },
          }),
        }),
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 38,
          color: '#1C1B1F',
          ...theme.applyStyles('dark', {
            color: '#FFFFFF',
          }),
        }),
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: '#1C1B1F',
          ...theme.applyStyles('dark', {
            color: '#FFFFFF',
          }),
        }),
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 'calc(60px + env(safe-area-inset-bottom))',
          minHeight: 'calc(60px + env(safe-area-inset-bottom))',
          position: 'fixed' as const,
          paddingBottom: 'env(safe-area-inset-bottom)',
          bottom: 0, left: 0, right: 0,
          backgroundColor: system_bar_colors.light.navigation,
          ...theme.applyStyles('dark', {
            backgroundColor: system_bar_colors.dark.navigation,
          }),
        }),
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: '#1C1B1F',
          ...theme.applyStyles('dark', {
            color: '#FFFFFF',
          }),
        }),
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...theme.applyStyles('dark', {
            backgroundColor: '#232223',
            backgroundImage: 'none',
          }),
        }),
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...theme.applyStyles('dark', {
            backgroundColor: '#232223',
            backgroundImage: 'none',
          }),
        }),
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSnackbarContent-root': {
            background: 'white',
            color: 'black',
            boxShadow: '0 4px 4px 0 rgba(0,0,0,0.2)',
            borderRadius: '4px',
            ...theme.applyStyles('dark', {
              background: '#373637',
              color: '#FFFFFF',
            }),
          },
          '& .MuiSnackbarContent-message': {
            wordBreak: 'break-word',
          },
        }),
        ...snackbar_position_overrides,
      }
    },
  },
})

export const default_theme = FEATURE_DARK_MODE ? dark_mode_theme : light_theme
